import { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';

import { CustomComponentName, PlatformAppUiSchema, ValuesOf } from '@typings';
import { CUSTOM_COMPONENT_NAME } from '@constants';
import { as, isNumber, normalizePlatformAppsFormErrors } from '@utils';

import { JobPresetField } from '@components/Job';
import { Field } from '@components/Shared';

import { PlatformAppField } from '.';

const HIDDEN_FIELDS = ['valueFrom.secretKeyRef.name'];

/**
 * Formatter fixes case with `anyOf` schemas.
 * When `anyOf` state is changed, form parsed value is set to `null` on register.
 * That triggers validation error for primitive types
 */
const formatter = {
  number: (value: any) => {
    const normalizedValue = Number(value);
    const isEmpty = value === '' || value === null;

    return isNumber(normalizedValue) &&
      !isEmpty &&
      !Number.isNaN(normalizedValue)
      ? normalizedValue
      : undefined;
  },
  string: (value: any = '') => (value === null ? '' : value),
  boolean: (value: any) => (value === null ? false : value),
};

type Props = {
  withFormatter?: boolean;
  name: string;
  typeName?: string;
  properties: PlatformAppUiSchema.FieldProperties;
};

export const PlatformAppsComponentMap: React.FC<Props> = ({
  withFormatter = true,
  name,
  typeName,
  properties: { title, description, required, type, options, items },
}) => {
  const { register, formState } = useFormContext();

  const isHiddenField = HIDDEN_FIELDS.some((fieldName) =>
    new RegExp(fieldName).test(name),
  );
  const { [name]: error } = normalizePlatformAppsFormErrors(formState.errors);
  const fieldName = name.split('.').at(-1);

  const isCustomComponent = ([
    customTypeName,
    customFieldName,
  ]: CustomComponentName) =>
    customTypeName === typeName && customFieldName === fieldName;

  const getFormatter = (formatFn: ValuesOf<typeof formatter>) =>
    withFormatter ? formatFn : undefined;

  const componentByType = (() => {
    switch (type) {
      case 'boolean':
        return (
          <Field.Checkbox
            {...register(name, { setValueAs: getFormatter(formatter.boolean) })}
            required={required}
            note={description}
            error={error}
            label={title}
          >
            {title}
          </Field.Checkbox>
        );
      case 'string':
        return (
          <Field.Input
            {...register(name, { setValueAs: getFormatter(formatter.string) })}
            required={required}
            label={title}
            note={description}
            error={error}
          />
        );
      case 'array':
        return (
          <PlatformAppField.Array
            name={name}
            required={required}
            label={title}
            note={description}
            error={error}
            items={items}
          />
        );
      case 'enum': {
        const selectOptions = as
          .a<string[]>(options)
          .map((value) => ({ value, text: value }));

        return (
          <Field.Select
            {...register(name)}
            name={name}
            required={required}
            label={title}
            note={description}
            options={selectOptions}
          />
        );
      }
      case 'integer':
      case 'number':
      case 'float': {
        const options = withFormatter
          ? { setValueAs: getFormatter(formatter.number) }
          : { valueAsNumber: true };

        return (
          <Field.Input
            {...register(name, options)}
            type="number"
            inputMode="numeric"
            required={required}
            label={title}
            note={description}
            error={error}
          />
        );
      }

      default:
        return null;
    }
  })();

  const customComponent = (() => {
    if (isCustomComponent(CUSTOM_COMPONENT_NAME.PRESET)) {
      return (
        <JobPresetField
          required={required}
          name={name}
          note={description}
          error={error}
        />
      );
    }

    if (isCustomComponent(CUSTOM_COMPONENT_NAME.SECRET)) {
      return (
        <PlatformAppField.Secret
          required={required}
          name={name}
          title={title}
          description={description}
        />
      );
    }

    if (isCustomComponent(CUSTOM_COMPONENT_NAME.STORAGE_PATH)) {
      return (
        <PlatformAppField.StoragePath
          required={required}
          name={name}
          title={title}
          description={description}
        />
      );
    }

    return null;
  })();

  const component = customComponent ?? componentByType;
  const isComponentSupported = !!component;

  useEffect(() => {
    if (isHiddenField && name) {
      register(name);
    }
  }, [name, isHiddenField, register]);

  useEffect(() => {
    if (!isComponentSupported && !isHiddenField) {
      // eslint-disable-next-line no-console
      console.log(`Component ${name} is not supported`);
    }
  }, [name, isHiddenField, isComponentSupported]);

  if (isHiddenField) {
    return null;
  }

  return component;
};
